/**
 * Bancos de nevoeiro: manchas densas que atravessam o jardim devagar, levadas
 * pelo vento, a respirar (crescem e encolhem). No meio de um banco o nevoeiro
 * é quase opaco — quem passa por lá desaparece e volta a aparecer do outro
 * lado.
 *
 * Os bancos estão presos ao MUNDO (não ao ecrã): andar não os arrasta, e
 * todos os clientes os vêem nos mesmos sítios à mesma hora, porque a posição
 * só depende do relógio. Só se vêem lá fora (a máscara é a do resto do tempo).
 */
import { MAP_WIDTH, SURFACE_ROWS, TILE } from "@greenvoice/shared";
import type PhaserT from "phaser";

/** Onde nasce cada banco (px), a que velocidade anda (px/s) e o tamanho. */
export const BANCOS = [
  { x: 120, y: 60, vel: 9, tam: 1.2 },
  { x: 700, y: 30, vel: 7, tam: 1 },
  { x: 60, y: 420, vel: 11, tam: 1.1 },
  { x: 1180, y: 520, vel: 8, tam: 1.25 },
  { x: 300, y: 840, vel: 10, tam: 1.3 },
  { x: 950, y: 900, vel: 6, tam: 1.15 },
  { x: 500, y: 1080, vel: 12, tam: 1.2 },
  { x: 200, y: 1300, vel: 8, tam: 1.35 },
  { x: 1000, y: 1250, vel: 9, tam: 1.1 },
  { x: 650, y: 1500, vel: 7, tam: 1.3 },
] as const;

/** A largura por onde os bancos dão a volta: saem por um lado e entram pelo outro. */
const VOLTA = MAP_WIDTH + 900;

export interface PoseDoBanco {
  x: number;
  y: number;
  escala: number;
  alfa: number;
}

/** Onde está o banco `i` no instante `t` (ms desde a época). */
export function bancoNoInstante(i: number, t: number): PoseDoBanco {
  const b = BANCOS[i] ?? BANCOS[0];
  const s = t / 1000;
  const x = ((((b.x + b.vel * s + 450) % VOLTA) + VOLTA) % VOLTA) - 450;
  const y = b.y + Math.sin(s / 11 + i * 1.7) * 36;
  const respira = Math.sin(s / 7 + i * 2.3);
  return {
    x,
    y: Math.min(SURFACE_ROWS * TILE - 40, Math.max(20, y)),
    escala: b.tam * (1 + 0.12 * respira),
    alfa: 0.88 + 0.12 * respira,
  };
}

/** As três manchas que fazem um banco, relativas ao centro dele (px e escala). */
const MANCHAS = [
  { dx: -110, dy: 12, sx: 1.5, sy: 1.1 },
  { dx: 0, dy: -18, sx: 1.8, sy: 1.3 },
  { dx: 120, dy: 10, sx: 1.4, sy: 1 },
] as const;

const T_BANCO = "clima:banco";

function textura(scene: PhaserT.Scene): void {
  if (scene.textures.exists(T_BANCO)) return;
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext("2d");
  if (!ctx) return;
  // Miolo opaco e borda larga e macia: ao entrar no banco, a pessoa vai-se
  // apagando em vez de desaparecer de repente.
  const g = ctx.createRadialGradient(128, 128, 8, 128, 128, 128);
  g.addColorStop(0, "rgba(232, 237, 242, 0.99)");
  g.addColorStop(0.45, "rgba(232, 237, 242, 0.95)");
  g.addColorStop(0.75, "rgba(232, 237, 242, 0.5)");
  g.addColorStop(1, "rgba(232, 237, 242, 0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  scene.textures.addCanvas(T_BANCO, c);
}

export class BancosDeNevoeiro {
  private readonly manchas: { img: PhaserT.GameObjects.Image; banco: number; mancha: number }[] = [];

  constructor(scene: PhaserT.Scene, profundidade: number, mascara: PhaserT.Display.Masks.GeometryMask) {
    textura(scene);
    BANCOS.forEach((_, banco) => {
      MANCHAS.forEach((_m, mancha) => {
        this.manchas.push({
          banco,
          mancha,
          img: scene.add.image(0, 0, T_BANCO).setDepth(profundidade).setAlpha(0).setVisible(false).setMask(mascara),
        });
      });
    });
  }

  /** Um frame. `nevoa` é a intensidade do nevoeiro (0-1). */
  update(nevoa: number, t: number = Date.now()): void {
    const visivel = nevoa > 0.005;
    for (const { img, banco, mancha } of this.manchas) {
      img.setVisible(visivel);
      if (!visivel) continue;
      const pose = bancoNoInstante(banco, t);
      const m = MANCHAS[mancha] ?? MANCHAS[0];
      // Cada mancha respira com o seu próprio atraso: o banco muda de forma.
      const propria = 1 + 0.08 * Math.sin(t / 3000 + banco + mancha * 1.3);
      img
        .setPosition(pose.x + m.dx * pose.escala, pose.y + m.dy * pose.escala)
        .setScale(m.sx * pose.escala * propria, m.sy * pose.escala * propria)
        .setAlpha(nevoa * pose.alfa);
    }
  }
}
